import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { usePortal } from '../../context/PortalContext';
import { useToast } from '../../context/ToastContext';
import { useApi } from '../../utils/api';
import PatrolDetailModal from '../OfficerDashboard/tabs/PatrolDetailModal';
import styles from './AdminDashboard.module.css';

/**
 * AdminDashboard
 * 
 * Admin-only dashboard rendered when user is admin.
 * Shows 9 admin tabs for portal management.
 * 
 */
function AdminDashboard({ activeTab }) {
  const { portal_name, primary_color, enable_dark_mode, pro_unlocked } = useConfig();
  const { state, dispatch } = usePortal();
  const { pushToast } = useToast();
  const api = useApi();

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragState, setDragState] = useState({ listKey: '', draggedId: null, overId: null });
  const [dragAnnouncement, setDragAnnouncement] = useState('');

  const [shiftOrder, setShiftOrder] = useState(() => state.shifts.map((item) => item.id));
  const [categoryOrder, setCategoryOrder] = useState(() => state.categories.map((item) => item.id));
  const [rankOrder, setRankOrder] = useState(() => state.ranks.map((item) => item.id));

  // General Settings form state — seed from server-side window.LEP payload
  const [generalSettings, setGeneralSettings] = useState({
    warning_message: window.LEP?.warning_message || '',
    officer_can_view_dept: !!window.LEP?.officer_can_view_dept,
    legend_trigger_len: window.LEP?.legend_trigger_len ?? 12,
    portal_slug: window.LEP?.portal_slug || 'law-enforcement-portal',
  });

  // Officers form state
  const [editingOfficer, setEditingOfficer] = useState(null);
  const [newOfficer, setNewOfficer] = useState({
    name: '',
    rank: '',
    role: '',
    shift: '',
    username: '',
    code: ''
  });

  // Categories form state
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    abbreviation: ''
  });

  // Shifts form state
  const [editingShift, setEditingShift] = useState(null);
  const [newShift, setNewShift] = useState({
    name: ''
  });

  // Appearance form state
  const [appearanceSettings, setAppearanceSettings] = useState({
    dst_surface: window.LEP?.dst_surface || '#ffffff',
    dst_table_bg: window.LEP?.dst_table_bg || '#ffffff',
    dst_table_text: window.LEP?.dst_table_text || '#222222',
    dst_table_head: window.LEP?.dst_table_head || '#e9f6ff',
    dst_table_head_text: window.LEP?.dst_table_head_text || '#1741a8',
    dst_table_border: window.LEP?.dst_table_border || '#dddddd',
    dst_shift_total_bg: window.LEP?.dst_shift_total_bg || '#e8f4ea',
    dst_shift_total_text: window.LEP?.dst_shift_total_text || '#157b46',
    dst_dept_total_bg: window.LEP?.dst_dept_total_bg || '#e9fbe9',
    dst_dept_total_text: window.LEP?.dst_dept_total_text || '#255b21',
    dst_portal_main_bg: window.LEP?.dst_portal_main_bg || '#ffffff',
    dst_tabs_bar_bg: window.LEP?.dst_tabs_bar_bg || '#ffffff',
    dst_portal_text_color: window.LEP?.dst_portal_text_color || '#222222',
    dst_appearance_title: window.LEP?.dst_appearance_title || 'Department Stats Portal',
    dst_appearance_bg: window.LEP?.dst_appearance_bg || '#ededed',
    dst_appearance_text: window.LEP?.dst_appearance_text || '#222222',
    dst_appearance_logo: window.LEP?.dst_appearance_logo || '',
  });
  const [appearanceLogoFile, setAppearanceLogoFile] = useState(null);
  const [removeAppearanceLogo, setRemoveAppearanceLogo] = useState(false);

  // Patrol types form state
  const [patrolTypes, setPatrolTypes] = useState([]);
  const [newPatrolType, setNewPatrolType] = useState('');
  const [patrolAreas, setPatrolAreas] = useState(() => Array.isArray(window.LEP?.patrol_city_filters) ? window.LEP.patrol_city_filters : []);

  const [patrolEdit, setPatrolEdit] = useState({
    request_id: '',
    type_id: '',
    address: '',
    city: '',
    notes: '',
    priority: 'normal',
    status: 'active',
  });
  const [patrolRows, setPatrolRows] = useState([]);
  const [patrolsLoading, setPatrolsLoading] = useState(false);
  const [patrolChecksRows, setPatrolChecksRows] = useState([]);
  const [patrolChecksLoading, setPatrolChecksLoading] = useState(false);
  const [patrolExportFilters, setPatrolExportFilters] = useState({ from: '', to: '', type_id: '' });
  const [patrolDetailModal, setPatrolDetailModal] = useState(null);
  const [patrolDetailLoadingId, setPatrolDetailLoadingId] = useState(null);
  const patrolTableWrapRef = useRef(null);
  const officerEditFormRef = useRef(null);
  const officerEditNameInputRef = useRef(null);
  const patrolEditFormRef = useRef(null);
  const patrolEditAddressInputRef = useRef(null);

  const [requestFilters, setRequestFilters] = useState({ status: '', officer_id: '', category_id: '' });
  const [requestRows, setRequestRows] = useState([]);
  const [requestNotes, setRequestNotes] = useState({});
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [viewStatsFilters, setViewStatsFilters] = useState({ officer_id: '', category_id: '', stat_date: '' });
  const [viewStatsResult, setViewStatsResult] = useState(null);
  const [apiResult, setApiResult] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [previewColor, setPreviewColor] = useState(primary_color || '#205ec8');

  // Logs state
  const [logsRows, setLogsRows] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsActions, setLogsActions] = useState([]);
  const [logsFilters, setLogsFilters] = useState({ action_filter: '', date_from: '', date_to: '' });

  // API Views state
  const [apiViews, setApiViews] = useState([]);
  const [apiViewsLoading, setApiViewsLoading] = useState(false);
  const [apiViewFilter, setApiViewFilter] = useState('');
  const [apiViewShowAdd, setApiViewShowAdd] = useState(false);
  const [apiViewEditId, setApiViewEditId] = useState(null);
  const [apiViewExpandId, setApiViewExpandId] = useState(null);
  const defaultApiViewForm = {
    label: '', is_enabled: true, category_ids: [], officer_ids: [], shift_ids: [],
    default_range: 'month', custom_start: '', custom_end: '',
    breakdown_by_officer: false, show_dept_total: false, allowed_ips: '',
  };
  const [apiViewForm, setApiViewForm] = useState(defaultApiViewForm);

  const idToLabel = (items, id) => {
    const match = items.find((item) => String(item.id) === String(id));
    return match?.name || match?.label || match?.title || `#${id}`;
  };

  const orderedRows = (items, order) => {
    if (!Array.isArray(items) || !Array.isArray(order)) return [];
    return order
      .map((id) => items.find((item) => String(item.id) === String(id)))
      .filter(Boolean);
  };

  const moveOrderItem = (order, index, direction) => {
    const next = [...order];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= next.length) return next;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    return next;
  };

  const reorderByDrag = (order, draggedId, overId) => {
    const fromIndex = order.findIndex((id) => String(id) === String(draggedId));
    const toIndex = order.findIndex((id) => String(id) === String(overId));

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      return order;
    }

    const next = [...order];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  };

  const clearStatus = () => {
    setMessage('');
    setError('');
  };

  const withStatus = async (actionLabel, callback) => {
    clearStatus();
    setBusy(true);
    try {
      const result = await callback();
      setMessage(`${actionLabel} completed successfully.`);
      pushToast(`${actionLabel} completed successfully.`, 'success');
      setActivityLog((prev) => [
        {
          id: Date.now(),
          time: new Date().toLocaleString(),
          action: actionLabel,
        },
        ...prev,
      ].slice(0, 15));
      return result;
    } catch (err) {
      setError(err.message || `${actionLabel} failed.`);
      pushToast(err.message || `${actionLabel} failed.`, 'error');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const summaryCards = useMemo(() => ([
    { label: 'Portal Name', value: portal_name || 'Law Enforcement Portal' },
    { label: 'Primary Color', value: previewColor },
    { label: 'Dark Mode', value: enable_dark_mode ? 'Enabled' : 'Disabled' },
    { label: 'Officers', value: String(state.officers.length) },
    { label: 'Shifts', value: String(state.shifts.length) },
    { label: 'Categories', value: String(state.categories.length) },
  ]), [enable_dark_mode, portal_name, previewColor, state.categories.length, state.officers.length, state.shifts.length]);

  useEffect(() => {
    if (activeTab === 'change-requests') {
      loadChangeRequests();
    }
    if (activeTab === 'patrols-admin') {
      loadPatrolRequests();
      loadPatrolChecks();
      loadPatrolTypes();
      if (patrolTableWrapRef.current) {
        patrolTableWrapRef.current.scrollLeft = 0;
      }
    }
    if (activeTab === 'api-views') {
      loadApiViews();
    }
    if (activeTab === 'logs') {
      loadLogs();
    }
  }, [activeTab]);

  const loadPatrolRequests = async () => {
    setPatrolsLoading(true);
    clearStatus();
    try {
      const data = await api.getAdminPatrolRequests();
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setPatrolRows(rows);
      requestAnimationFrame(() => {
        if (patrolTableWrapRef.current) {
          patrolTableWrapRef.current.scrollLeft = 0;
        }
      });
    } catch (err) {
      setError(err.message || 'Failed to load patrol requests.');
    } finally {
      setPatrolsLoading(false);
    }
  };

  const loadPatrolChecks = async () => {
    setPatrolChecksLoading(true);
    try {
      const data = await api.recentPatrolChecks();
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setPatrolChecksRows(rows.slice(0, 50));
    } catch (err) {
      setError(err.message || 'Failed to load recent patrol checks.');
    } finally {
      setPatrolChecksLoading(false);
    }
  };

  const loadPatrolTypes = async () => {
    try {
      const data = await api.getAdminPatrolTypes();
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const normalized = rows.map((row) => ({
        ...row,
        id: Number(row.id),
        active: Number(row.active) === 1,
      }));
      setPatrolTypes(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load patrol types.');
    }
  };

  const openPatrolDetail = async (requestId) => {
    if (!requestId) return;
    setPatrolDetailLoadingId(requestId);
    try {
      const data = await api.getPatrolDetail(requestId);
      if (data?.request) {
        setPatrolDetailModal(data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load patrol details.');
    } finally {
      setPatrolDetailLoadingId(null);
    }
  };

  const buildAjaxUrl = (params = {}) => {
    const base = window.LEP?.api_base || window.LEP?.apiBase || '/wp-admin/admin-ajax.php';
    const url = new URL(base, window.location.origin);
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v) !== '') {
        url.searchParams.set(k, String(v));
      }
    });
    return url.toString();
  };

  const exportPatrolRequestsCsv = () => {
    const url = buildAjaxUrl({
      action: 'dst_patrol_export_csv',
      nonce: window.LEP?.nonce || '',
      from: patrolExportFilters.from,
      to: patrolExportFilters.to,
      type_id: patrolExportFilters.type_id,
    });
    window.location.href = url;
  };

  const exportPatrolChecksCsv = () => {
    const url = buildAjaxUrl({
      action: 'lep_patrol_checks_export_csv',
      nonce: window.LEP?.nonce || '',
      from: patrolExportFilters.from,
      to: patrolExportFilters.to,
    });
    window.location.href = url;
  };

  const scrollToEditor = (containerRef, inputRef) => {
    const scrollNow = () => {
      const node = containerRef.current;
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Fallback for layouts where scrollIntoView targets an inner container.
        const top = node.getBoundingClientRect().top + window.scrollY - 24;
        if (Number.isFinite(top)) {
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      }
      inputRef.current?.focus();
    };

    requestAnimationFrame(scrollNow);
    setTimeout(scrollNow, 120);
  };

  const selectOfficerForEdit = (officer) => {
    if (!officer) return;
    setEditingOfficer(officer);

    const label = officer.name || `#${officer.id}`;
    setMessage(`Loaded officer ${label} into the editor.`);
    scrollToEditor(officerEditFormRef, officerEditNameInputRef);
  };

  const selectPatrolForEdit = (row) => {
    if (!row) return;
    setPatrolEdit({
      request_id: String(row.id || ''),
      type_id: String(row.type_id || ''),
      address: row.address || '',
      city: row.city || '',
      notes: row.notes || '',
      priority: row.priority || 'normal',
      status: row.status === 'removed' ? 'inactive' : 'active',
    });

    const reqId = row.id ? `#${row.id}` : 'request';
    setMessage(`Loaded patrol ${reqId} into the editor.`);
    scrollToEditor(patrolEditFormRef, patrolEditAddressInputRef);
  };

  const loadChangeRequests = async () => {
    setRequestsLoading(true);
    clearStatus();
    try {
      const data = await api.getAdminChangeRequests(requestFilters);
      const rows = Array.isArray(data?.requests) ? data.requests : [];
      setRequestRows(rows);

      const nextNotes = {};
      rows.forEach((row) => {
        nextNotes[row.id] = row.admin_note || '';
      });
      setRequestNotes(nextNotes);
    } catch (err) {
      setError(err.message || 'Failed to load change requests.');
    } finally {
      setRequestsLoading(false);
    }
  };

  const renderGeneralTab = () => (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>General Settings</h3>
      <div className={styles.grid}>
        {summaryCards.map((card) => (
          <div key={card.label} className={styles.card}>
            <div className={styles.cardLabel}>{card.label}</div>
            <div className={styles.cardValue}>{card.value}</div>
          </div>
        ))}
      </div>
      <p className={styles.helpText}>These values are loaded from server-side options and your localized LEP bootstrap payload.</p>

      <div className={styles.card}>
        <h4>System-wide Settings</h4>
        <label className={styles.field}>
          <span>Warning Message</span>
          <input
            type="text"
            value={generalSettings.warning_message}
            onChange={(e) => setGeneralSettings((prev) => ({ ...prev, warning_message: e.target.value }))}
            placeholder="Enter a system warning message"
            style={{ width: '100%' }}
          />
        </label>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={generalSettings.officer_can_view_dept}
            onChange={(e) => setGeneralSettings((prev) => ({ ...prev, officer_can_view_dept: e.target.checked }))}
          />
          Allow officers to view department-wide stats
        </label>
        <label className={styles.field}>
          <span>Legend Trigger Length (characters)</span>
          <input
            type="number"
            min="0"
            value={generalSettings.legend_trigger_len}
            onChange={(e) => setGeneralSettings((prev) => ({ ...prev, legend_trigger_len: parseInt(e.target.value) }))}
            style={{ width: '120px' }}
          />
          <span className={styles.helpText}>If a category abbreviation/name exceeds this length, it will be included in the legend.</span>
        </label>
        <label className={styles.field}>
          <span>Portal Page Slug</span>
          <input
            type="text"
            value={generalSettings.portal_slug}
            onChange={(e) => setGeneralSettings((prev) => ({ ...prev, portal_slug: e.target.value }))}
            style={{ width: '100%', maxWidth: '340px' }}
            placeholder="law-enforcement-portal"
          />
          <span className={styles.helpText}>URL path for your portal page. Do not include slashes.</span>
        </label>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={busy}
            onClick={() => withStatus('Saved general settings', async () => {
              const saved = await api.saveGeneralSettings(generalSettings);
              dispatch({ type: 'SET_GENERAL_SETTINGS', payload: saved });
            })}
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );

  const renderOrderEditor = (title, listKey, order, setOrder, items, onSave) => (
    <div className={styles.card}>
      <div className={styles.cardLabel}>{title}</div>
      <p id={`${listKey}-help`} className={styles.helpText}>Drag and drop rows to reorder. Use arrow keys while focused on a row for keyboard fallback.</p>
      <div className={styles.stack} role="list" aria-label={title} aria-describedby={`${listKey}-help`}>
        {orderedRows(items, order).map((row, idx) => (
          <div
            key={`${title}-${row.id}`}
            className={`${styles.orderRow} ${styles.draggableRow} ${dragState.listKey === listKey && String(dragState.draggedId) === String(row.id) ? styles.draggingRow : ''} ${dragState.listKey === listKey && String(dragState.overId) === String(row.id) ? styles.dropTargetRow : ''}`}
            draggable={!busy}
            role="listitem"
            aria-label={`${title} item ${idx + 1}`}
            tabIndex={0}
            onKeyDown={(event) => {
              if (busy) return;
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setOrder((prev) => moveOrderItem(prev, idx, 'up'));
                setDragAnnouncement(`Moved ${row.name || row.rank || row.label || `item ${idx + 1}`} up.`);
              }
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setOrder((prev) => moveOrderItem(prev, idx, 'down'));
                setDragAnnouncement(`Moved ${row.name || row.rank || row.label || `item ${idx + 1}`} down.`);
              }
            }}
            onDragStart={() => {
              setDragState({ listKey, draggedId: row.id, overId: row.id });
              setDragAnnouncement(`Dragging ${row.name || row.rank || row.label || `item ${idx + 1}`}.`);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragState.listKey === listKey && String(dragState.overId) !== String(row.id)) {
                setDragState((prev) => ({ ...prev, overId: row.id }));
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              setOrder((prev) => reorderByDrag(prev, dragState.draggedId, row.id));
              setDragAnnouncement(`Moved ${row.name || row.rank || row.label || `item ${idx + 1}`}.`);
              setDragState({ listKey: '', draggedId: null, overId: null });
            }}
            onDragEnd={() => {
              setDragState({ listKey: '', draggedId: null, overId: null });
            }}
          >
            <span>
              <span className={styles.dragHandle} aria-hidden="true">::</span>
              {idx + 1}. {row.name || row.rank || row.label || `#${row.id}`}
            </span>
            <div className={styles.rowActions}>
              <button
                type="button"
                className={styles.smallButton}
                onClick={() => setOrder((prev) => moveOrderItem(prev, idx, 'up'))}
                disabled={idx === 0 || busy}
              >
                Up
              </button>
              <button
                type="button"
                className={styles.smallButton}
                onClick={() => setOrder((prev) => moveOrderItem(prev, idx, 'down'))}
                disabled={idx === order.length - 1 || busy}
              >
                Down
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        className={styles.primaryButton}
        disabled={busy || order.length === 0}
        onClick={() => withStatus(`Saved ${title}`, () => onSave(order))}
      >
        Save {title}
      </button>
    </div>
  );

  const renderOfficersTab = () => (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Officers, Categories, and Sort Order</h3>
      
      {/* Add New Officer */}
      <div className={styles.card}>
        <h4>Add New Officer</h4>
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Name</span>
            <input
              type="text"
              value={newOfficer.name}
              onChange={(e) => setNewOfficer((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <label className={styles.field}>
            <span>Rank</span>
            <input
              type="text"
              value={newOfficer.rank}
              onChange={(e) => setNewOfficer((prev) => ({ ...prev, rank: e.target.value }))}
              placeholder="Deputy, Sergeant, etc."
            />
          </label>
          <label className={styles.field}>
            <span>Username</span>
            <input
              type="text"
              value={newOfficer.username}
              onChange={(e) => setNewOfficer((prev) => ({ ...prev, username: e.target.value }))}
            />
          </label>
          <label className={styles.field}>
            <span>Officer Code (Passcode)</span>
            <input
              type="text"
              value={newOfficer.code}
              onChange={(e) => setNewOfficer((prev) => ({ ...prev, code: e.target.value }))}
              placeholder="4-6 digits"
            />
          </label>
          <label className={styles.field}>
            <span>Shift</span>
            <select
              value={newOfficer.shift}
              onChange={(e) => setNewOfficer((prev) => ({ ...prev, shift: e.target.value }))}
            >
              <option value="">Select a shift</option>
              {state.shifts.map((shift) => (
                <option key={shift.id} value={shift.name}>{shift.name}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span>Role</span>
            <input
              type="text"
              value={newOfficer.role}
              onChange={(e) => setNewOfficer((prev) => ({ ...prev, role: e.target.value }))}
            />
          </label>
        </div>
        <button
          type="button"
          className={styles.primaryButton}
          disabled={busy || !newOfficer.name || !newOfficer.username || !newOfficer.code}
          onClick={() => withStatus('Officer added', async () => {
            const officer = { ...newOfficer };
            const saved = await api.addOfficer(officer);
            const { code: _officerCode, ...visibleOfficer } = officer;
            dispatch({ type: 'SET_OFFICERS', payload: [...state.officers, { ...visibleOfficer, id: saved.id }] });
            setNewOfficer({ name: '', rank: '', role: '', shift: '', username: '', code: '' });
          })}
        >
          Add Officer
        </button>
      </div>

      {/* Officer Directory */}
      <div className={styles.card}>
        <h4>Officer Directory ({state.officers.length}/25)</h4>
        {state.officers.length >= 25 && (
          <div className={styles.noticeBox} style={{ backgroundColor: '#fff3cd', color: '#856404' }}>
            <strong>Officer Limit Reached:</strong> The free version supports maximum 25 officers.
          </div>
        )}
        <div ref={patrolTableWrapRef} className={styles.tableWrap}>
          <table className={`${styles.table} ${styles.compactTable}`}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Rank</th>
                <th>Shift</th>
                <th>Username</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.officers.map((officer) => (
                <tr key={officer.id}>
                  <td>{officer.name}</td>
                  <td>{officer.rank || '—'}</td>
                  <td>{officer.shift || '—'}</td>
                  <td><code>{officer.username}</code></td>
                  <td>
                    <button
                      type="button"
                      className={styles.smallButton}
                      disabled={busy}
                      onClick={() => selectOfficerForEdit(officer)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles.dangerButton}
                      disabled={busy}
                      onClick={async () => {
                        if (!window.confirm(`Delete officer ${officer.name}? This cannot be undone.`)) return;
                        await withStatus('Officer deleted', async () => {
                          await api.deleteOfficer(officer.id);
                          dispatch({ type: 'SET_OFFICERS', payload: state.officers.filter((item) => String(item.id) !== String(officer.id)) });
                        });
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Officer Modal/Form */}
      {editingOfficer && (
        <div ref={officerEditFormRef} className={styles.card} style={{ borderLeft: '3px solid var(--dst-primary, #205ec8)' }}>
          <h4>Edit Officer: {editingOfficer.name}</h4>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>Name</span>
              <input
                ref={officerEditNameInputRef}
                type="text"
                value={editingOfficer.name || ''}
                onChange={(e) => setEditingOfficer((prev) => ({ ...prev, name: e.target.value }))}
              />
            </label>
            <label className={styles.field}>
              <span>Rank</span>
              <select
                value={editingOfficer.rank || ''}
                onChange={(e) => setEditingOfficer((prev) => ({ ...prev, rank: e.target.value }))}
              >
                <option value="">— select rank —</option>
                {state.ranks.map((r) => (
                  <option key={r.id} value={r.name}>{r.name}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Username</span>
              <input
                type="text"
                value={editingOfficer.username || ''}
                onChange={(e) => setEditingOfficer((prev) => ({ ...prev, username: e.target.value }))}
              />
            </label>
            <label className={styles.field}>
              <span>New Officer Code / Passcode (leave blank to keep current)</span>
              <input
                type="password"
                autoComplete="new-password"
                value={editingOfficer.officer_code || ''}
                onChange={(e) => setEditingOfficer((prev) => ({ ...prev, officer_code: e.target.value }))}
              />
            </label>
            <label className={styles.field}>
              <span>Shift</span>
              <select
                value={editingOfficer.shift || ''}
                onChange={(e) => setEditingOfficer((prev) => ({ ...prev, shift: e.target.value }))}
              >
                <option value="">— select shift —</option>
                {state.shifts.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Role</span>
              <select
                value={editingOfficer.role || ''}
                onChange={(e) => setEditingOfficer((prev) => ({ ...prev, role: e.target.value }))}
              >
                <option value="">— select role —</option>
                <option value="officer">Officer</option>
                <option value="admin">Admin</option>
                <option value="supervisor">Supervisor</option>
                <option value="dispatcher">Dispatcher</option>
              </select>
            </label>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={busy}
              onClick={async () => {
                await withStatus('Officer updated', async () => {
                  const updated = { ...editingOfficer };
                  await api.editOfficer(updated.id, updated);
                  delete updated.officer_code;
                  dispatch({
                    type: 'SET_OFFICERS',
                    payload: state.officers.map((item) => String(item.id) === String(updated.id) ? { ...item, ...updated } : item),
                  });
                  setEditingOfficer(null);
                });
              }}
            >
              Save Changes
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={busy}
              onClick={() => setEditingOfficer(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Ordering Editors */}
      <div className={styles.gridThree}>
        {renderOrderEditor('Shift Order', 'shift-order', shiftOrder, setShiftOrder, state.shifts, api.saveShiftOrder)}
        {renderOrderEditor('Category Order', 'category-order', categoryOrder, setCategoryOrder, state.categories, api.saveCategoryOrder)}
        {renderOrderEditor('Rank Order', 'rank-order', rankOrder, setRankOrder, state.ranks, api.saveRankOrder)}
      </div>
    </div>
  );

  const renderPatrolsTab = () => {
    const visiblePatrolRows = patrolRows.filter((row) => !row.archived_at);
    const archivedPatrolRows = patrolRows.filter((row) => Boolean(row.archived_at));

    return (
    <div className={styles.section}>
      <h3 className={`${styles.sectionTitle} ${styles.patrolPageTitle}`}>Patrol Management</h3>
      
      <div className={styles.card}>
        <h4>Patrol Request Types</h4>
        <p className={styles.helpText}>Create custom patrol types that officers can assign to requests.</p>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <input
            type="text"
            value={newPatrolType}
            onChange={(e) => setNewPatrolType(e.target.value)}
            placeholder="Enter new patrol type (e.g., 'Routine', 'Welfare Check')"
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid var(--dst-input-border)',
              background: 'var(--dst-input-bg)',
              color: 'var(--dst-text)',
            }}
          />
          <button
            type="button"
            className={styles.primaryButton}
            disabled={busy || !newPatrolType.trim()}
            onClick={() => {
              withStatus('Added patrol type', async () => {
                await api.addPatrolType(newPatrolType.trim());
                setNewPatrolType('');
                await loadPatrolTypes();
              });
            }}
          >
            Add Type
          </button>
        </div>
        {patrolTypes.length > 0 && (
          <div className={styles.stack}>
            {patrolTypes.map((type) => (
              <div key={type.id} className={styles.listRow} style={{ justifyContent: 'space-between' }}>
                <span>{type.name}</span>
                <div className={styles.inlineActions}>
                  <button
                    type="button"
                    className={styles.smallButton}
                    disabled={busy}
                    onClick={() => withStatus(type.active ? 'Patrol type disabled' : 'Patrol type enabled', async () => {
                      await api.togglePatrolType(type.id, type.active ? 0 : 1);
                      await loadPatrolTypes();
                    })}
                  >
                    {type.active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    className={styles.dangerButton}
                    disabled={busy}
                    onClick={() => withStatus('Patrol type deleted', async () => {
                      await api.deletePatrolType(type.id);
                      await Promise.all([loadPatrolTypes(), loadPatrolRequests()]);
                    })}
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.card}>
        <h4>Patrol Area Filter Buttons</h4>
        <p className={styles.helpText}>Add the buttons deputies will see above Requested Patrols. Enter one city or a comma-separated list; matching ignores capitalization and extra spaces.</p>
        <div className={styles.stack}>
          {patrolAreas.map((area, index) => (
            <div key={`patrol-area-${index}`} className={styles.formGrid}>
              <label className={styles.field}>
                <span>Button name</span>
                <input value={area.label || ''} onChange={(e) => setPatrolAreas((rows) => rows.map((row, i) => i === index ? { ...row, label: e.target.value } : row))} placeholder="City 1" />
              </label>
              <label className={styles.field}>
                <span>Cities to match</span>
                <input value={area.city || ''} onChange={(e) => setPatrolAreas((rows) => rows.map((row, i) => i === index ? { ...row, city: e.target.value } : row))} placeholder="Alexandria, Pineville" />
              </label>
              <button type="button" className={styles.dangerButton} disabled={busy} onClick={() => setPatrolAreas((rows) => rows.filter((_, i) => i !== index))}>Remove</button>
            </div>
          ))}
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.secondaryButton} disabled={busy || patrolAreas.length >= 20} onClick={() => setPatrolAreas((rows) => [...rows, { label: '', city: '' }])}>+ Add Area Button</button>
          <button type="button" className={styles.primaryButton} disabled={busy} onClick={() => withStatus('Saved patrol area buttons', async () => {
            const saved = await api.savePatrolAreas(patrolAreas);
            setPatrolAreas(saved.areas || []);
            window.LEP.patrol_city_filters = saved.areas || [];
          })}>Save Area Buttons</button>
        </div>
      </div>

      {Array.from(new Map(
        visiblePatrolRows.map((r) => [String(r.type_id || '0'), { id: String(r.type_id || '0'), name: r.type_name || 'Unknown' }])
      ).values()).map((type) => {
        const rows = visiblePatrolRows.filter((r) => String(r.type_id || '0') === String(type.id));
        return (
          <details key={`group-${type.id}`} className={styles.card}>
            <summary className={styles.archiveSummary}>{type.name} Requests ({rows.length})</summary>
            <div className={styles.tableWrap}>
              <table className={`${styles.table} ${styles.compactTable}`}>
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Address</th>
                    <th scope="col">City</th>
                    <th scope="col">Notes (preview)</th>
                    <th scope="col">Updated</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={6} className={styles.emptyCell}>None</td></tr>
                  )}
                  {rows.map((row) => (
                    <tr key={`group-row-${row.id}`}>
                      <td>#{row.id}</td>
                      <td>{row.address || '—'}</td>
                      <td>{row.city || '—'}</td>
                      <td>{row.notes ? String(row.notes).slice(0, 120) : '—'}</td>
                      <td>{row.updated_at || row.created_at || '—'}</td>
                      <td className={styles.actionsCell}>
                        <div className={styles.inlineActions}>
                          <button
                            type="button"
                            className={styles.smallButton}
                            disabled={busy || patrolDetailLoadingId === row.id}
                            onClick={() => openPatrolDetail(row.id)}
                          >
                            {patrolDetailLoadingId === row.id ? 'Loading...' : 'View'}
                          </button>
                          <button type="button" className={styles.smallButton} disabled={busy} onClick={() => selectPatrolForEdit(row)}>Edit</button>
                          <button
                            type="button"
                            className={styles.smallButton}
                            disabled={busy}
                            onClick={() => withStatus('Patrol status updated', async () => {
                              await api.togglePatrolActive(row.id, row.status === 'removed' ? 'active' : 'inactive');
                              await loadPatrolRequests();
                            })}
                          >
                            {row.status === 'removed' ? 'Activate' : 'Deactivate'}
                          </button>
                          {row.status === 'removed' && (
                            <button
                              type="button"
                              className={styles.dangerButton}
                              disabled={busy}
                              onClick={async () => {
                                if (!window.confirm(`Archive disabled patrol request #${row.id}?`)) return;
                                await withStatus('Patrol request archived', async () => {
                                  await api.archivePatrolRequest(row.id);
                                  await loadPatrolRequests();
                                });
                              }}
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        );
      })}

      {archivedPatrolRows.length > 0 && (
        <details className={styles.card}>
          <summary className={styles.archiveSummary}>
            Archived Patrol Requests ({archivedPatrolRows.length})
          </summary>
          <p className={styles.helpText}>Archived requests remain disabled. Restore one to return it to the main list.</p>
          <div className={styles.tableWrap}>
            <table className={`${styles.table} ${styles.compactTable}`}>
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Type</th>
                  <th scope="col">Address</th>
                  <th scope="col">City</th>
                  <th scope="col">Archived</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {archivedPatrolRows.map((row) => (
                  <tr key={`archived-row-${row.id}`}>
                    <td>#{row.id}</td>
                    <td>{row.type_name || 'Unknown'}</td>
                    <td>{row.address || '—'}</td>
                    <td>{row.city || '—'}</td>
                    <td>{row.archived_at || '—'}</td>
                    <td className={styles.actionsCell}>
                      <div className={styles.inlineActions}>
                        <button
                          type="button"
                          className={styles.smallButton}
                          disabled={busy || patrolDetailLoadingId === row.id}
                          onClick={() => openPatrolDetail(row.id)}
                        >
                          {patrolDetailLoadingId === row.id ? 'Loading...' : 'View'}
                        </button>
                        <button
                          type="button"
                          className={styles.smallButton}
                          disabled={busy}
                          onClick={() => withStatus('Patrol request restored', async () => {
                            await api.restorePatrolRequest(row.id);
                            await loadPatrolRequests();
                          })}
                        >
                          Restore
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {!pro_unlocked && (
        <div className={styles.noticeBox}>
          <strong>Pro Feature:</strong> Filter and Export tools are available in Pro.
        </div>
      )}

      {pro_unlocked && (
        <div className={styles.card}>
          <h4>Filter & Export</h4>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>From</span>
              <input
                type="date"
                value={patrolExportFilters.from}
                onChange={(e) => setPatrolExportFilters((prev) => ({ ...prev, from: e.target.value }))}
              />
            </label>
            <label className={styles.field}>
              <span>To</span>
              <input
                type="date"
                value={patrolExportFilters.to}
                onChange={(e) => setPatrolExportFilters((prev) => ({ ...prev, to: e.target.value }))}
              />
            </label>
            <label className={styles.field}>
              <span>Type</span>
              <select
                value={patrolExportFilters.type_id}
                onChange={(e) => setPatrolExportFilters((prev) => ({ ...prev, type_id: e.target.value }))}
              >
                <option value="">All</option>
                {Array.from(new Map(
                  patrolRows
                    .filter((r) => String(r.type_id || '') !== '' && (r.type_name || '').trim() !== '')
                    .map((r) => [String(r.type_id), { id: String(r.type_id), name: r.type_name }])
                ).values()).map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={exportPatrolRequestsCsv}
            >
              Export Requests CSV
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={exportPatrolChecksCsv}
            >
              Export Checks CSV
            </button>
          </div>
        </div>
      )}

      <div className={styles.card}>
        <h4>Recent Patrol Checks</h4>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={busy || patrolChecksLoading}
            onClick={loadPatrolChecks}
          >
            {patrolChecksLoading ? 'Loading...' : 'Refresh Recent Checks'}
          </button>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className={styles.caption}>Recent patrol check-ins recorded in the system.</caption>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Req #</th>
                <th scope="col">Type</th>
                <th scope="col">Address</th>
                <th scope="col">City</th>
                <th scope="col">Action</th>
                <th scope="col">Officer</th>
                <th scope="col">Note (preview)</th>
                <th scope="col">View</th>
              </tr>
            </thead>
            <tbody>
              {!patrolChecksLoading && patrolChecksRows.length === 0 && (
                <tr>
                  <td colSpan={9} className={styles.emptyCell}>No recent checks found.</td>
                </tr>
              )}
              {patrolChecksRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.created_at || '—'}</td>
                  <td>#{row.request_id || '—'}</td>
                  <td>{row.type_name || '—'}</td>
                  <td>{row.address || '—'}</td>
                  <td>{row.city || '—'}</td>
                  <td>{row.action_text || 'Checked'}</td>
                  <td>{row.officer_label || row.officer_name || '—'}</td>
                  <td>{row.note ? String(row.note).slice(0, 80) : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.smallButton}
                      disabled={busy || !row.request_id || patrolDetailLoadingId === row.request_id}
                      onClick={() => openPatrolDetail(row.request_id)}
                    >
                      {patrolDetailLoadingId === row.request_id ? 'Loading...' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className={`${styles.card} ${styles.patrolEditor}`} ref={patrolEditFormRef}>
        <h4>Create/Edit Patrol Request</h4>
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Request ID</span>
            <input type="number" value={patrolEdit.request_id} onChange={(e) => setPatrolEdit((prev) => ({ ...prev, request_id: e.target.value }))} placeholder="Leave blank for new request" />
          </label>
          <label className={styles.field}>
            <span>Status</span>
            <select value={patrolEdit.status} onChange={(e) => setPatrolEdit((prev) => ({ ...prev, status: e.target.value }))}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <label className={styles.field}>
            <span>Patrol Type</span>
            <select value={patrolEdit.type_id} onChange={(e) => setPatrolEdit((prev) => ({ ...prev, type_id: e.target.value }))}>
              <option value="">Select a type</option>
              {patrolTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span>Priority</span>
            <select value={patrolEdit.priority} onChange={(e) => setPatrolEdit((prev) => ({ ...prev, priority: e.target.value }))}>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </label>
          <label className={styles.fieldWide}>
            <span>Address</span>
            <input ref={patrolEditAddressInputRef} type="text" value={patrolEdit.address} onChange={(e) => setPatrolEdit((prev) => ({ ...prev, address: e.target.value }))} placeholder="Street address" />
          </label>
          <label className={styles.field}>
            <span>City</span>
            <input type="text" value={patrolEdit.city} onChange={(e) => setPatrolEdit((prev) => ({ ...prev, city: e.target.value }))} />
          </label>
          <label className={styles.fieldWide}>
            <span>Notes</span>
            <textarea rows={3} value={patrolEdit.notes} onChange={(e) => setPatrolEdit((prev) => ({ ...prev, notes: e.target.value }))} />
          </label>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={busy || !patrolEdit.address || !patrolEdit.type_id}
            onClick={() => withStatus(patrolEdit.request_id ? 'Updated patrol request' : 'Created patrol request', async () => {
              if (patrolEdit.request_id) {
                await api.updatePatrolRequest(
                  patrolEdit.request_id,
                  patrolEdit.type_id,
                  patrolEdit.address,
                  patrolEdit.city,
                  patrolEdit.notes,
                  patrolEdit.priority,
                );
              } else {
                await api.createPatrolRequest({
                  type_id: patrolEdit.type_id,
                  address: patrolEdit.address,
                  city: patrolEdit.city,
                  notes: patrolEdit.notes,
                  priority: patrolEdit.priority,
                });
              }
              await loadPatrolRequests();
              pushToast(patrolEdit.request_id ? 'Patrol request updated.' : 'Patrol request created.', 'success');
              if (!patrolEdit.request_id) {
                setPatrolEdit({ request_id: '', type_id: '', address: '', city: '', notes: '', priority: 'normal', status: 'active' });
              }
            })}
          >
            Save Request
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={busy || !patrolEdit.request_id}
            onClick={() => withStatus('Toggled patrol request status', async () => {
              await api.togglePatrolActive(patrolEdit.request_id, patrolEdit.status);
              await loadPatrolRequests();
            })}
          >
            Toggle Status
          </button>
          <button
            type="button"
            className={styles.dangerButton}
            disabled={busy || !patrolEdit.request_id}
            onClick={async () => {
              const confirmed = window.confirm('Remove this patrol request from active lists?');
              if (!confirmed) return;
              await withStatus('Deleted patrol request', async () => {
                await api.deletePatrolRequest(patrolEdit.request_id);
                await loadPatrolRequests();
                setPatrolEdit({ request_id: '', type_id: '', address: '', city: '', notes: '', priority: 'normal', status: 'active' });
              });
            }}
          >
            Delete Request
          </button>
        </div>
      </div>

      {patrolDetailModal && (
        <PatrolDetailModal
          data={patrolDetailModal}
          onClose={() => setPatrolDetailModal(null)}
        />
      )}
    </div>
    );
  };

  const renderRequestsTab = () => (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Change Requests Moderation</h3>
      <div className={styles.formGrid}>
        <label className={styles.field}>
          <span>Status</span>
          <select
            value={requestFilters.status}
            onChange={(e) => setRequestFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
        </label>
        <label className={styles.field}>
          <span>Officer</span>
          <select
            value={requestFilters.officer_id}
            onChange={(e) => setRequestFilters((prev) => ({ ...prev, officer_id: e.target.value }))}
          >
            <option value="">All officers</option>
            {state.officers.map((officer) => (
              <option key={officer.id} value={officer.id}>{officer.name || `Officer #${officer.id}`}</option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span>Category</span>
          <select
            value={requestFilters.category_id}
            onChange={(e) => setRequestFilters((prev) => ({ ...prev, category_id: e.target.value }))}
          >
            <option value="">All categories</option>
            {state.categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name || `Category #${category.id}`}</option>
            ))}
          </select>
        </label>
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primaryButton}
          disabled={busy || requestsLoading}
          onClick={loadChangeRequests}
        >
          {requestsLoading ? 'Loading...' : 'Load Requests'}
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <caption className={styles.caption}>Change requests moderation queue.</caption>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Officer</th>
              <th scope="col">Category</th>
              <th scope="col">Old</th>
              <th scope="col">New</th>
              <th scope="col">Status</th>
              <th scope="col">Reason / Note</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requestRows.length === 0 && !requestsLoading && (
              <tr>
                <td colSpan={8} className={styles.emptyCell}>No change requests found.</td>
              </tr>
            )}
            {requestRows.map((row) => {
              const pending = row.status === 'pending';
              return (
                <tr key={row.id}>
                  <td>{row.stat_date || row.change_date || row.created_at || row.request_date || 'N/A'}</td>
                  <td>{row.officer_name || `Officer #${row.officer_id}`}</td>
                  <td>{row.category_name || `Category #${row.category_id}`}{row.value_field === 'mileage_begin' ? ' — Beginning Mileage' : row.value_field === 'mileage_end' ? ' — Ending Mileage' : ''}</td>
                  <td>{row.old_value}</td>
                  <td>{row.new_value}</td>
                  <td>{row.status}</td>
                  <td>
                    <input
                      className={styles.inlineInput}
                      value={requestNotes[row.id] || ''}
                      onChange={(e) => setRequestNotes((prev) => ({ ...prev, [row.id]: e.target.value }))}
                      disabled={!pending || busy}
                      placeholder="Admin note"
                    />
                  </td>
                  <td>
                    <div className={styles.inlineActions}>
                      <button
                        type="button"
                        className={styles.smallButton}
                        disabled={!pending || busy}
                        onClick={() => withStatus('Approved change request', async () => {
                          await api.moderateChangeRequest(row.id, 'approve', requestNotes[row.id] || '');
                          await loadChangeRequests();
                        })}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className={styles.dangerButton}
                        disabled={!pending || busy}
                        onClick={async () => {
                          if (!window.confirm('Deny this change request?')) return;
                          await withStatus('Denied change request', async () => {
                            await api.moderateChangeRequest(row.id, 'deny', requestNotes[row.id] || '');
                            await loadChangeRequests();
                          });
                        }}
                      >
                        Deny
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={styles.helpText}>Current schema stores the original officer reason and admin note in the same field (`admin_note`).</p>
    </div>
  );

  const renderViewStatsTab = () => (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>View Stats</h3>
      <div className={styles.formGrid}>
        <label className={styles.field}>
          <span>Officer</span>
          <select
            value={viewStatsFilters.officer_id}
            onChange={(e) => setViewStatsFilters((prev) => ({ ...prev, officer_id: e.target.value }))}
          >
            <option value="">All officers</option>
            {state.officers.map((officer) => (
              <option key={officer.id} value={officer.id}>{officer.name || `Officer #${officer.id}`}</option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span>Category</span>
          <select
            value={viewStatsFilters.category_id}
            onChange={(e) => setViewStatsFilters((prev) => ({ ...prev, category_id: e.target.value }))}
          >
            <option value="">All categories</option>
            {state.categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name || idToLabel(state.categories, category.id)}</option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span>Date</span>
          <input
            type="date"
            value={viewStatsFilters.stat_date}
            onChange={(e) => setViewStatsFilters((prev) => ({ ...prev, stat_date: e.target.value }))}
          />
        </label>
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primaryButton}
          disabled={busy}
          onClick={() => withStatus('Fetched stats view', async () => {
            const data = await api.getViewStatsTable(viewStatsFilters);
            setViewStatsResult(data);
            return data;
          })}
        >
          Refresh Table
        </button>
      </div>
      {viewStatsResult && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Date</th><th>Officer</th><th>Category</th><th>Beginning Mileage</th><th>Ending Mileage</th><th>Stat Total</th></tr></thead>
            <tbody>
              {(viewStatsResult.rows || []).map((row) => (
                <tr key={row.id}><td>{row.stat_date}</td><td>{row.officer_name || `Officer #${row.officer_id}`}</td><td>{row.category_name}</td><td>{row.mileage_begin ?? '—'}</td><td>{row.mileage_end ?? '—'}</td><td>{row.stat_value}</td></tr>
              ))}
              {(viewStatsResult.rows || []).length === 0 && <tr><td colSpan={6} className={styles.emptyCell}>No stats found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderAppearanceTab = () => {
    const setColor = (key) => (e) => setAppearanceSettings((prev) => ({ ...prev, [key]: e.target.value }));

    const saveAppearance = async () => {
      const payload = new FormData();
      Object.entries(appearanceSettings).forEach(([key, value]) => {
        if (key !== 'dst_appearance_logo') {
          payload.append(key, value ?? '');
        }
      });

      if (removeAppearanceLogo) {
        payload.append('remove_logo', '1');
      } else if (appearanceLogoFile) {
        payload.append('dst_appearance_logo', appearanceLogoFile);
      }

      const data = await api.saveAppearance(payload);
      const saved = data?.appearance || {};
      setAppearanceSettings((prev) => ({
        ...prev,
        ...saved,
        dst_appearance_logo: saved.dst_appearance_logo ?? prev.dst_appearance_logo,
      }));
      setAppearanceLogoFile(null);
      if (removeAppearanceLogo) {
        setRemoveAppearanceLogo(false);
      }
      return data;
    };

    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Appearance & Color Scheme</h3>

        <div className={styles.card}>
          <h4>Login Branding</h4>
          <label className={styles.field}>
            <span>Login Screen Title</span>
            <input
              type="text"
              value={appearanceSettings.dst_appearance_title || ''}
              onChange={(e) => setAppearanceSettings((prev) => ({ ...prev, dst_appearance_title: e.target.value }))}
              placeholder="Department Stats Portal"
            />
          </label>
          <div className={styles.colorGrid}>
            <div className={styles.colorField}>
              <label htmlFor="appearance_bg_color">
                <span>Login Background</span>
                <input id="appearance_bg_color" type="color" value={appearanceSettings.dst_appearance_bg || '#ededed'} onChange={setColor('dst_appearance_bg')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_appearance_bg || '#ededed'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="appearance_text_color">
                <span>Login Text Color</span>
                <input id="appearance_text_color" type="color" value={appearanceSettings.dst_appearance_text || '#222222'} onChange={setColor('dst_appearance_text')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_appearance_text || '#222222'}</span>
              </label>
            </div>
          </div>

          <label className={styles.field}>
            <span>Login Logo</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const nextFile = e.target.files?.[0] || null;
                setAppearanceLogoFile(nextFile);
                if (nextFile) {
                  setRemoveAppearanceLogo(false);
                }
              }}
            />
            {appearanceSettings.dst_appearance_logo ? (
              <span className={styles.helpText}>Current logo: {appearanceSettings.dst_appearance_logo}</span>
            ) : (
              <span className={styles.helpText}>No logo uploaded.</span>
            )}
          </label>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={removeAppearanceLogo}
              onChange={(e) => {
                setRemoveAppearanceLogo(e.target.checked);
                if (e.target.checked) {
                  setAppearanceLogoFile(null);
                }
              }}
            />
            Remove current logo
          </label>
        </div>

        <div className={styles.card}>
          <h4>Portal & Table Colors</h4>
          <p className={styles.helpText}>All appearance values now mirror the legacy wp-admin Appearance options.</p>

          <div className={styles.colorGrid}>
            <div className={styles.colorField}>
              <label htmlFor="surface_color">
                <span>Base Surface</span>
                <input id="surface_color" type="color" value={appearanceSettings.dst_surface || '#ffffff'} onChange={setColor('dst_surface')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_surface || '#ffffff'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="portal_main_bg_color">
                <span>Portal Main Background</span>
                <input id="portal_main_bg_color" type="color" value={appearanceSettings.dst_portal_main_bg || '#ffffff'} onChange={setColor('dst_portal_main_bg')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_portal_main_bg || '#ffffff'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="tabs_bar_bg_color">
                <span>Tabs Bar Background</span>
                <input id="tabs_bar_bg_color" type="color" value={appearanceSettings.dst_tabs_bar_bg || '#ffffff'} onChange={setColor('dst_tabs_bar_bg')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_tabs_bar_bg || '#ffffff'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="portal_text_color">
                <span>Portal Text Color</span>
                <input id="portal_text_color" type="color" value={appearanceSettings.dst_portal_text_color || '#222222'} onChange={setColor('dst_portal_text_color')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_portal_text_color || '#222222'}</span>
              </label>
            </div>

            <div className={styles.colorField}>
              <label htmlFor="table_bg_color">
                <span>Table Background</span>
                <input id="table_bg_color" type="color" value={appearanceSettings.dst_table_bg || '#ffffff'} onChange={setColor('dst_table_bg')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_table_bg || '#ffffff'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="table_text_color">
                <span>Table Text</span>
                <input id="table_text_color" type="color" value={appearanceSettings.dst_table_text || '#222222'} onChange={setColor('dst_table_text')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_table_text || '#222222'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="table_head_color">
                <span>Table Header Background</span>
                <input id="table_head_color" type="color" value={appearanceSettings.dst_table_head || '#e9f6ff'} onChange={setColor('dst_table_head')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_table_head || '#e9f6ff'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="table_head_text_color">
                <span>Table Header Text</span>
                <input id="table_head_text_color" type="color" value={appearanceSettings.dst_table_head_text || '#1741a8'} onChange={setColor('dst_table_head_text')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_table_head_text || '#1741a8'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="table_border_color">
                <span>Table Border</span>
                <input id="table_border_color" type="color" value={appearanceSettings.dst_table_border || '#dddddd'} onChange={setColor('dst_table_border')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_table_border || '#dddddd'}</span>
              </label>
            </div>

            <div className={styles.colorField}>
              <label htmlFor="shift_total_bg_color">
                <span>Shift Total Background</span>
                <input id="shift_total_bg_color" type="color" value={appearanceSettings.dst_shift_total_bg || '#e8f4ea'} onChange={setColor('dst_shift_total_bg')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_shift_total_bg || '#e8f4ea'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="shift_total_text_color">
                <span>Shift Total Text</span>
                <input id="shift_total_text_color" type="color" value={appearanceSettings.dst_shift_total_text || '#157b46'} onChange={setColor('dst_shift_total_text')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_shift_total_text || '#157b46'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="dept_total_bg_color">
                <span>Department Total Background</span>
                <input id="dept_total_bg_color" type="color" value={appearanceSettings.dst_dept_total_bg || '#e9fbe9'} onChange={setColor('dst_dept_total_bg')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_dept_total_bg || '#e9fbe9'}</span>
              </label>
            </div>
            <div className={styles.colorField}>
              <label htmlFor="dept_total_text_color">
                <span>Department Total Text</span>
                <input id="dept_total_text_color" type="color" value={appearanceSettings.dst_dept_total_text || '#255b21'} onChange={setColor('dst_dept_total_text')} />
                <span className={styles.colorValue}>{appearanceSettings.dst_dept_total_text || '#255b21'}</span>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <h4>Live Preview</h4>
          <div
            className={styles.previewTable}
            style={{
              backgroundColor: appearanceSettings.dst_table_bg || '#ffffff',
              border: `1px solid ${appearanceSettings.dst_table_border || '#dddddd'}`,
              borderRadius: '6px',
              overflow: 'hidden',
              color: appearanceSettings.dst_table_text || '#222222',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: appearanceSettings.dst_table_head || '#e9f6ff', color: appearanceSettings.dst_table_head_text || '#1741a8' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: `1px solid ${appearanceSettings.dst_table_border || '#dddddd'}` }}>Shift</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: `1px solid ${appearanceSettings.dst_table_border || '#dddddd'}` }}>Total</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: `1px solid ${appearanceSettings.dst_table_border || '#dddddd'}` }}>Department</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '12px', borderBottom: `1px solid ${appearanceSettings.dst_table_border || '#dddddd'}` }}>Alpha</td>
                  <td style={{ padding: '12px', borderBottom: `1px solid ${appearanceSettings.dst_shift_total_bg || '#e8f4ea'}`, backgroundColor: appearanceSettings.dst_shift_total_bg || '#e8f4ea', color: appearanceSettings.dst_shift_total_text || '#157b46' }}>18</td>
                  <td style={{ padding: '12px', borderBottom: `1px solid ${appearanceSettings.dst_dept_total_bg || '#e9fbe9'}`, backgroundColor: appearanceSettings.dst_dept_total_bg || '#e9fbe9', color: appearanceSettings.dst_dept_total_text || '#255b21' }}>53</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={busy}
              onClick={() => withStatus('Updated appearance settings', saveAppearance)}
            >
              Save Appearance Settings
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={busy}
              onClick={() => {
                setAppearanceSettings({
                  dst_surface: '#ffffff',
                  dst_table_bg: '#ffffff',
                  dst_table_text: '#222222',
                  dst_table_head: '#e9f6ff',
                  dst_table_head_text: '#1741a8',
                  dst_table_border: '#dddddd',
                  dst_shift_total_bg: '#e8f4ea',
                  dst_shift_total_text: '#157b46',
                  dst_dept_total_bg: '#e9fbe9',
                  dst_dept_total_text: '#255b21',
                  dst_portal_main_bg: '#ffffff',
                  dst_tabs_bar_bg: '#ffffff',
                  dst_portal_text_color: '#222222',
                  dst_appearance_title: 'Department Stats Portal',
                  dst_appearance_bg: '#ededed',
                  dst_appearance_text: '#222222',
                  dst_appearance_logo: '',
                });
                setAppearanceLogoFile(null);
                setRemoveAppearanceLogo(false);
              }}
            >
              Reset to Defaults
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLogsTab = () => {
    const handleLogsFilter = (e) => {
      e.preventDefault();
      loadLogs(logsFilters);
    };
    const handleLogsClear = () => {
      const reset = { action_filter: '', date_from: '', date_to: '' };
      setLogsFilters(reset);
      loadLogs(reset);
    };
    const renderDetails = (row) => {
      const parsed = row.details_parsed;
      if (!parsed) return <span className={styles.muted}>—</span>;
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        return (
          <ul className={styles.detailsList}>
            {Object.entries(parsed).map(([k, v]) => (
              <li key={k}><strong>{k}:</strong> {String(v)}</li>
            ))}
          </ul>
        );
      }
      return <span>{String(parsed)}</span>;
    };
    const inputStyle = { minHeight: 36, border: '1px solid var(--dst-input-border)', borderRadius: 6, padding: '6px 10px', background: 'var(--dst-input-bg)', color: 'var(--dst-text)', fontSize: '0.9rem' };
    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Department Stats Logs</h3>
        <form style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }} onSubmit={handleLogsFilter}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--dst-text-muted)' }}>Action</label>
            <select
              value={logsFilters.action_filter}
              onChange={(e) => setLogsFilters((f) => ({ ...f, action_filter: e.target.value }))}
              style={{ ...inputStyle, minWidth: 160 }}
            >
              <option value="">All actions</option>
              {logsActions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--dst-text-muted)' }}>From</label>
            <input
              type="date"
              value={logsFilters.date_from}
              onChange={(e) => setLogsFilters((f) => ({ ...f, date_from: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--dst-text-muted)' }}>To</label>
            <input
              type="date"
              value={logsFilters.date_to}
              onChange={(e) => setLogsFilters((f) => ({ ...f, date_to: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <button type="submit" className={styles.primaryButton}>Filter</button>
          <button type="button" className={styles.secondaryButton} onClick={handleLogsClear}>Clear</button>
        </form>
        {logsLoading ? (
          <div className={styles.muted}>Loading…</div>
        ) : logsRows.length === 0 ? (
          <div className={styles.muted}>No log entries found.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>Action</th>
                  <th>User</th>
                  <th>Officer ID</th>
                  <th>IP Address</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logsRows.map((row) => (
                  <tr key={row.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{row.created_at}</td>
                    <td>{row.action}</td>
                    <td>{row.username || <span className={styles.muted}>—</span>}</td>
                    <td>{row.officer_id || <span className={styles.muted}>—</span>}</td>
                    <td>{row.ip_address || <span className={styles.muted}>—</span>}</td>
                    <td>{renderDetails(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const loadApiViews = async () => {
    setApiViewsLoading(true);
    try {
      const data = await api.listApiViews();
      setApiViews(Array.isArray(data?.views) ? data.views : []);
    } catch (err) {
      setError(err.message || 'Failed to load API views.');
    } finally {
      setApiViewsLoading(false);
    }
  };

  const loadLogs = async (filters = logsFilters) => {
    setLogsLoading(true);
    clearStatus();
    try {
      const data = await api.getLogs(filters);
      setLogsRows(Array.isArray(data?.logs) ? data.logs : []);
      if (Array.isArray(data?.actions)) setLogsActions(data.actions);
    } catch (err) {
      setError(err.message || 'Failed to load logs.');
    } finally {
      setLogsLoading(false);
    }
  };

  const renderApiViewForm = (formTitle, onSubmit) => {
    const setField = (key) => (e) => setApiViewForm((prev) => ({ ...prev, [key]: e.target.value }));
    const toggleMulti = (key, val) => setApiViewForm((prev) => {
      const arr = prev[key] || [];
      const next = arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
      return { ...prev, [key]: next };
    });
    return (
      <div className={styles.card}>
        <h4>{formTitle}</h4>
        <label className={styles.field}>
          <span>Label / Description</span>
          <input type="text" value={apiViewForm.label} onChange={setField('label')} placeholder="e.g. Squad Room TV" />
        </label>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <span>Categories (multi-select)</span>
            <div style={{ border: '1px solid var(--dst-input-border)', borderRadius: 8, padding: 8, maxHeight: 120, overflowY: 'auto', background: 'var(--dst-input-bg)' }}>
              {state.categories.length === 0 && <span className={styles.muted}>No categories</span>}
              {state.categories.map((c) => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', cursor: 'pointer', color: 'var(--dst-text)', fontSize: '0.88rem' }}>
                  <input type="checkbox" checked={(apiViewForm.category_ids || []).includes(c.id)} onChange={() => toggleMulti('category_ids', c.id)} />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
          <div className={styles.field}>
            <span>Officers (multi-select)</span>
            <div style={{ border: '1px solid var(--dst-input-border)', borderRadius: 8, padding: 8, maxHeight: 120, overflowY: 'auto', background: 'var(--dst-input-bg)' }}>
              {state.officers.length === 0 && <span className={styles.muted}>No officers</span>}
              {state.officers.map((o) => (
                <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', cursor: 'pointer', color: 'var(--dst-text)', fontSize: '0.88rem' }}>
                  <input type="checkbox" checked={(apiViewForm.officer_ids || []).includes(o.id)} onChange={() => toggleMulti('officer_ids', o.id)} />
                  {o.name}
                </label>
              ))}
            </div>
          </div>
          <div className={styles.field}>
            <span>Shifts (multi-select)</span>
            <div style={{ border: '1px solid var(--dst-input-border)', borderRadius: 8, padding: 8, maxHeight: 120, overflowY: 'auto', background: 'var(--dst-input-bg)' }}>
              {state.shifts.length === 0 && <span className={styles.muted}>No shifts</span>}
              {state.shifts.map((s) => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', cursor: 'pointer', color: 'var(--dst-text)', fontSize: '0.88rem' }}>
                  <input type="checkbox" checked={(apiViewForm.shift_ids || []).includes(s.name)} onChange={() => toggleMulti('shift_ids', s.name)} />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
          <div className={styles.field}>
            <span>Default Timeframe</span>
            <select value={apiViewForm.default_range} onChange={setField('default_range')}>
              <option value="today">Current Day</option>
              <option value="week">Current Week</option>
              <option value="month">Current Month</option>
              <option value="year">Current Year</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range…</option>
            </select>
          </div>
          {apiViewForm.default_range === 'custom' && (
            <>
              <label className={styles.field}>
                <span>Custom Start Date</span>
                <input type="date" value={apiViewForm.custom_start} onChange={setField('custom_start')} />
              </label>
              <label className={styles.field}>
                <span>Custom End Date</span>
                <input type="date" value={apiViewForm.custom_end} onChange={setField('custom_end')} />
              </label>
            </>
          )}
        </div>
        <label className={styles.checkbox}>
          <input type="checkbox" checked={!!apiViewForm.breakdown_by_officer} onChange={(e) => setApiViewForm((prev) => ({ ...prev, breakdown_by_officer: e.target.checked }))} />
          Breakdown by Officer
        </label>
        <label className={styles.checkbox}>
          <input type="checkbox" checked={!!apiViewForm.show_dept_total} onChange={(e) => setApiViewForm((prev) => ({ ...prev, show_dept_total: e.target.checked }))} />
          Show Department Total
        </label>
        <label className={styles.checkbox}>
          <input type="checkbox" checked={!!apiViewForm.is_enabled} onChange={(e) => setApiViewForm((prev) => ({ ...prev, is_enabled: e.target.checked }))} />
          Enabled
        </label>
        <label className={styles.field}>
          <span>Allowed IPs (comma-separated, blank = no restriction)</span>
          <input type="text" value={apiViewForm.allowed_ips} onChange={setField('allowed_ips')} placeholder="e.g. 192.168.1.*,10.0.0.2" />
        </label>
        <div className={styles.actions}>
          <button type="button" className={styles.primaryButton} disabled={busy} onClick={onSubmit}>
            {formTitle}
          </button>
          <button type="button" className={styles.secondaryButton} disabled={busy} onClick={() => { setApiViewShowAdd(false); setApiViewEditId(null); setApiViewForm(defaultApiViewForm); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderApiTab = () => {
    const rangeLabel = (v) => {
      const map = { today: 'Day', week: 'Week', month: 'Month', year: 'Year', all: 'All Time' };
      if (v.ranges_type === 'custom') return `Custom: ${v.ranges_start} → ${v.ranges_end}`;
      return map[v.ranges_start] || v.ranges_start || '—';
    };

    const filteredViews = apiViews.filter((v) => {
      if (!apiViewFilter) return true;
      const q = apiViewFilter.toLowerCase();
      return (v.label || '').toLowerCase().includes(q) || (v.api_key || '').toLowerCase().includes(q);
    });

    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>API Views (Live Dashboards)</h3>
        <p className={styles.helpText}>Create live dashboards for squad room TVs, PowerPoint, or public stats. Each view generates an API endpoint, a WordPress page with a shortcode embed, and supports optional IP restrictions.</p>

        <div className={styles.actions}>
          <button type="button" className={styles.primaryButton} disabled={busy || apiViewsLoading}
            onClick={() => withStatus('Loaded API views', async () => { await loadApiViews(); })}
          >
            {apiViewsLoading ? 'Loading…' : 'Refresh Views'}
          </button>
          <button type="button" className={styles.secondaryButton} disabled={busy}
            onClick={() => { setApiViewShowAdd(!apiViewShowAdd); setApiViewEditId(null); setApiViewForm(defaultApiViewForm); }}
          >
            {apiViewShowAdd ? 'Cancel' : '+ Add New API View'}
          </button>
        </div>

        {apiViewShowAdd && renderApiViewForm('Add View', () => withStatus('Added API view', async () => {
          if (!apiViewForm.label) throw new Error('Label is required');
          const payload = {
            label: apiViewForm.label,
            is_enabled: apiViewForm.is_enabled ? 1 : 0,
            category_ids: apiViewForm.category_ids,
            officer_ids: apiViewForm.officer_ids,
            shift_ids: apiViewForm.shift_ids,
            default_range: apiViewForm.default_range,
            custom_start: apiViewForm.custom_start,
            custom_end: apiViewForm.custom_end,
            breakdown_by_officer: apiViewForm.breakdown_by_officer ? 1 : 0,
            show_dept_total: apiViewForm.show_dept_total ? 1 : 0,
            allowed_ips: apiViewForm.allowed_ips,
          };
          const data = await api.addApiView(payload);
          if (data?.view) setApiViews((prev) => [data.view, ...prev]);
          setApiViewShowAdd(false);
          setApiViewForm(defaultApiViewForm);
          return data;
        }))}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Filter views by label or API key…"
            value={apiViewFilter}
            onChange={(e) => setApiViewFilter(e.target.value)}
            style={{ minWidth: 260, maxWidth: 420, width: '100%', minHeight: 44, border: '1px solid var(--dst-input-border)', borderRadius: 8, padding: '9px 10px', background: 'var(--dst-input-bg)', color: 'var(--dst-text)', fontSize: '0.95rem' }}
          />
          <span className={styles.muted} style={{ fontSize: '0.85rem' }}>Type to filter by label or API key.</span>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>View</th>
                <th>Categories</th>
                <th>Officers</th>
                <th>Shifts</th>
                <th>Default Window</th>
                <th>Status</th>
                <th>API / Embed</th>
                <th>WP Page</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiViewsLoading && (
                <tr><td colSpan={9} className={styles.emptyCell}>Loading…</td></tr>
              )}
              {!apiViewsLoading && filteredViews.length === 0 && (
                <tr><td colSpan={9} className={styles.emptyCell}>{apiViews.length === 0 ? 'No API views yet. Click "+ Add New API View" to create one.' : 'No views match your filter.'}</td></tr>
              )}
              {filteredViews.map((v) => {
                const isEditing = apiViewEditId === v.id;
                return (
                  <React.Fragment key={v.id}>
                    <tr>
                      <td style={{ minWidth: 200 }}>
                        <strong style={{ color: 'var(--dst-text)' }}>{v.label}</strong>
                        <div style={{ fontSize: '0.78rem', marginTop: 2 }}>
                          <span style={{ color: 'var(--dst-muted)' }}>Key: </span>
                          <code style={{ fontSize: '0.78rem', color: 'var(--dst-text)', wordBreak: 'break-all' }}>{v.api_key}</code>
                          {' '}
                          <button type="button" className={styles.smallButton} style={{ padding: '2px 6px', minHeight: 'unset', fontSize: '0.75rem' }}
                            onClick={() => navigator.clipboard?.writeText(v.api_key)}>Copy</button>
                        </div>
                        <div style={{ color: 'var(--dst-muted)', fontSize: '0.75rem', marginTop: 2 }}>Created: {v.created_at || '—'}</div>
                      </td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--dst-text)' }}>{v.category_ids?.length ? v.category_ids.join(', ') : <em style={{ color: 'var(--dst-muted)' }}>All</em>}</td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--dst-text)' }}>{v.officer_ids?.length ? v.officer_ids.join(', ') : <em style={{ color: 'var(--dst-muted)' }}>All</em>}</td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--dst-text)' }}>{v.shift_ids?.length ? v.shift_ids.join(', ') : <em style={{ color: 'var(--dst-muted)' }}>All</em>}</td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--dst-text)', whiteSpace: 'nowrap' }}>{rangeLabel(v)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {v.is_enabled
                          ? <span style={{ display: 'inline-block', padding: '2px 8px', background: 'var(--dst-success-bg)', color: 'var(--dst-success-text)', border: '1px solid var(--dst-success-border)', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600 }}>Enabled</span>
                          : <span style={{ display: 'inline-block', padding: '2px 8px', background: 'var(--dst-error-bg)', color: 'var(--dst-error-text)', border: '1px solid var(--dst-error-border)', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600 }}>Disabled</span>
                        }
                        <div style={{ fontSize: '0.75rem', color: 'var(--dst-muted)', marginTop: 2 }}>
                          {v.breakdown_by_officer ? 'Breakdown: Yes' : 'Breakdown: No'}<br />
                          {v.show_dept_total ? 'Dept Total: Yes' : 'Dept Total: No'}
                        </div>
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div style={{ fontSize: '0.78rem', color: 'var(--dst-muted)' }}>API URL (All-time)</div>
                        <code style={{ fontSize: '0.75rem', wordBreak: 'break-all', color: 'var(--dst-text)' }}>{v.api_url}</code>
                        <div style={{ marginTop: 4 }}>
                          <button type="button" className={styles.smallButton} style={{ fontSize: '0.75rem', minHeight: 'unset', marginRight: 4 }}
                            onClick={() => navigator.clipboard?.writeText(v.api_url)}>Copy URL</button>
                          <button type="button" className={styles.smallButton} style={{ fontSize: '0.75rem', minHeight: 'unset' }}
                            onClick={() => setApiViewExpandId(apiViewExpandId === v.id ? null : v.id)}>Embed…</button>
                        </div>
                        {apiViewExpandId === v.id && (
                          <div style={{ marginTop: 6, fontSize: '0.78rem' }}>
                            <div style={{ color: 'var(--dst-muted)', marginBottom: 2 }}>Shortcode</div>
                            <textarea readOnly value={v.shortcode} rows={2}
                              style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.78rem', background: 'var(--dst-input-bg)', color: 'var(--dst-text)', border: '1px solid var(--dst-input-border)', borderRadius: 6, padding: 4, resize: 'none' }} />
                            <button type="button" className={styles.smallButton} style={{ fontSize: '0.75rem', minHeight: 'unset', marginTop: 2 }}
                              onClick={() => navigator.clipboard?.writeText(v.shortcode)}>Copy Shortcode</button>
                            <div style={{ color: 'var(--dst-muted)', marginTop: 6, marginBottom: 2 }}>Iframe</div>
                            <textarea readOnly value={`<iframe src="${v.api_url}" width="100%" height="600" frameborder="0"></iframe>`} rows={2}
                              style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.78rem', background: 'var(--dst-input-bg)', color: 'var(--dst-text)', border: '1px solid var(--dst-input-border)', borderRadius: 6, padding: 4, resize: 'none' }} />
                            <button type="button" className={styles.smallButton} style={{ fontSize: '0.75rem', minHeight: 'unset', marginTop: 2 }}
                              onClick={() => navigator.clipboard?.writeText(`<iframe src="${v.api_url}" width="100%" height="600" frameborder="0"></iframe>`)}>Copy Iframe</button>
                          </div>
                        )}
                      </td>
                      <td style={{ minWidth: 160 }}>
                        {v.page_url
                          ? <><a href={v.page_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.83rem', color: 'var(--dst-primary)' }}>View Page</a><br /><code style={{ fontSize: '0.72rem', color: 'var(--dst-muted)', wordBreak: 'break-all' }}>{v.page_url}</code></>
                          : <em style={{ color: 'var(--dst-muted)', fontSize: '0.83rem' }}>Not generated</em>
                        }
                        <div style={{ marginTop: 4 }}>
                          <button type="button" className={styles.smallButton} style={{ fontSize: '0.75rem', minHeight: 'unset' }} disabled={busy}
                            onClick={() => withStatus('Recreated page', async () => {
                              const data = await api.recreateApiViewPage(v.id);
                              if (data?.view) setApiViews((prev) => prev.map((r) => r.id === v.id ? data.view : r));
                              return data;
                            })}>Recreate Page</button>
                        </div>
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div className={styles.inlineActions} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                          <button type="button" className={styles.smallButton} disabled={busy}
                            onClick={() => withStatus(v.is_enabled ? 'Disabled view' : 'Enabled view', async () => {
                              const data = await api.toggleApiView(v.id, !v.is_enabled);
                              if (data?.view) setApiViews((prev) => prev.map((r) => r.id === v.id ? data.view : r));
                              return data;
                            })}>{v.is_enabled ? 'Disable' : 'Enable'}</button>
                          <button type="button" className={styles.smallButton} disabled={busy}
                            onClick={() => {
                              setApiViewEditId(isEditing ? null : v.id);
                              setApiViewShowAdd(false);
                              if (!isEditing) {
                                setApiViewForm({
                                  label: v.label,
                                  is_enabled: v.is_enabled,
                                  category_ids: v.category_ids || [],
                                  officer_ids: v.officer_ids || [],
                                  shift_ids: v.shift_ids || [],
                                  default_range: v.ranges_type === 'custom' ? 'custom' : (v.ranges_start || 'month'),
                                  custom_start: v.ranges_type === 'custom' ? v.ranges_start : '',
                                  custom_end: v.ranges_type === 'custom' ? v.ranges_end : '',
                                  breakdown_by_officer: v.breakdown_by_officer,
                                  show_dept_total: v.show_dept_total,
                                  allowed_ips: v.allowed_ips || '',
                                });
                              }
                            }}>{isEditing ? 'Cancel Edit' : 'Edit'}</button>
                          <button type="button" className={styles.smallButton} disabled={busy}
                            onClick={() => withStatus('Regenerated key', async () => {
                              if (!window.confirm('Regenerate API key? The old key will stop working and the WP page shortcode will be updated.')) return null;
                              const data = await api.regenApiViewKey(v.id);
                              if (data?.view) setApiViews((prev) => prev.map((r) => r.id === v.id ? data.view : r));
                              return data;
                            })}>Regen Key</button>
                          <button type="button" className={styles.dangerButton} style={{ fontSize: '0.78rem', padding: '4px 8px', minHeight: 'unset' }} disabled={busy}
                            onClick={() => withStatus('Deleted API view', async () => {
                              if (!window.confirm('Delete this API view? This cannot be undone.')) return null;
                              await api.deleteApiView(v.id);
                              setApiViews((prev) => prev.filter((r) => r.id !== v.id));
                            })}>Delete</button>
                        </div>
                      </td>
                    </tr>
                    {isEditing && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0 }}>
                          {renderApiViewForm('Save Changes', () => withStatus('Updated API view', async () => {
                            if (!apiViewForm.label) throw new Error('Label is required');
                            const payload = {
                              label: apiViewForm.label,
                              is_enabled: apiViewForm.is_enabled ? 1 : 0,
                              category_ids: apiViewForm.category_ids,
                              officer_ids: apiViewForm.officer_ids,
                              shift_ids: apiViewForm.shift_ids,
                              default_range: apiViewForm.default_range,
                              custom_start: apiViewForm.custom_start,
                              custom_end: apiViewForm.custom_end,
                              breakdown_by_officer: apiViewForm.breakdown_by_officer ? 1 : 0,
                              show_dept_total: apiViewForm.show_dept_total ? 1 : 0,
                              allowed_ips: apiViewForm.allowed_ips,
                            };
                            const data = await api.editApiView(v.id, payload);
                            if (data?.view) setApiViews((prev) => prev.map((r) => r.id === v.id ? data.view : r));
                            setApiViewEditId(null);
                            setApiViewForm(defaultApiViewForm);
                            return data;
                          }))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDocsTab = () => (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Documentation & Help</h3>
      
      <div className={styles.card}>
        <h4>License Overview</h4>
        <p className={styles.helpText}>This plugin includes both Free and Pro features.</p>
        <ul className={styles.docsList}>
          <li><strong>Free License:</strong> Includes General, Officers, Patrols, View Stats, Appearance, and Docs tabs. Officer limit: 25 per agency.</li>
          <li><strong>Pro License:</strong> Unlocks Change Requests, Logs, and API Views tabs. Unlimited officers and advanced features.</li>
          <li>License status and feature availability are determined by your WordPress installation's plugin licensing system.</li>
        </ul>
      </div>

      <div className={styles.card}>
        <h4>Quick Start Guide</h4>
        <ol className={styles.docsList}>
          <li><strong>Step 1:</strong> Go to the General tab and configure your agency's warning message and portal slug.</li>
          <li><strong>Step 2:</strong> Add officers in the Officers tab with appropriate rank and shift information.</li>
          <li><strong>Step 3:</strong> Create patrol request types in the Patrols tab (e.g., "Routine", "Welfare Check", etc.)</li>
          <li><strong>Step 4:</strong> Place the shortcode <code>[law-enforcement-portal]</code> on any page to display the officer portal.</li>
          <li><strong>Step 5:</strong> Customize appearance colors in the Appearance tab to match your agency brand.</li>
        </ol>
      </div>

      <div className={styles.card}>
        <h4>Tab Reference</h4>
        <div className={styles.docReference}>
          <div className={styles.refItem}>
            <h5>General Settings</h5>
            <p>Configure agency-wide defaults including warning message, department visibility settings, legend trigger length, and custom portal slug. These settings apply to all officers and affect portal behavior globally.</p>
          </div>
          <div className={styles.refItem}>
            <h5>Officers</h5>
            <p>Create, edit, and manage officer records. Assign each officer a rank, badge number, shift assignment, and system username. Drag to reorder officers, and use inline editing to update details quickly.</p>
          </div>
          <div className={styles.refItem}>
            <h5>Patrols</h5>
            <p>Manage patrol request types and create/edit individual patrol requests. Define custom request types, then assign patrol requests to officers with address, notes, and status tracking. Refreshable list shows all active requests.</p>
          </div>
          <div className={styles.refItem}>
            <h5>Department Stats</h5>
            <p>View roll-up reporting and visibility across all officers. See activity summaries, response times, and other aggregated metrics for department-wide analysis.</p>
          </div>
          <div className={styles.refItem}>
            <h5>Appearance</h5>
            <p>Customize the visual appearance of the portal. Set colors for table backgrounds, text, headers, borders, and primary action buttons. Live preview shows how changes will appear in the actual portal.</p>
          </div>
          <div className={styles.refItem}>
            <h5>Logs <span className={styles.badge}>Free/Pro</span></h5>
            <p>View recent administrative actions taken within the portal. Logs show who performed what action and when. Pro license provides more detailed logging and longer history.</p>
          </div>
          <div className={styles.refItem}>
            <h5>Change Requests <span className={styles.badge}>Pro Only</span></h5>
            <p>Pro feature for managing officer-submitted change requests. Approve or deny modifications to officer information with a full moderation interface.</p>
          </div>
          <div className={styles.refItem}>
            <h5>API Views <span className={styles.badge}>Pro Only</span></h5>
            <p>Pro feature for viewing API endpoint metrics and patrol check history. Monitor how the portal's data APIs are being accessed and used.</p>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <h4>Common Tasks</h4>
        <ul className={styles.docsList}>
          <li><strong>Add an Officer:</strong> Click "Add New Officer" in Officers tab, fill in details, select rank and shift, then save.</li>
          <li><strong>Create Patrol Type:</strong> Go to Patrols tab, enter a new type name, click Add Type, then use in patrol requests.</li>
          <li><strong>Update Appearance:</strong> Go to Appearance tab, adjust colors using color pickers, check live preview, then save.</li>
          <li><strong>View Department Stats:</strong> Click Department Stats tab to see roll-up reporting and activity summaries.</li>
          <li><strong>Edit Officer:</strong> Go to Officers tab, find officer in list, click Edit button, modify fields, then save changes.</li>
          <li><strong>Delete Officer:</strong> Click the delete button next to officer name (confirmation required). This is permanent.</li>
        </ul>
      </div>

      <div className={styles.card}>
        <h4>Troubleshooting</h4>
        <ul className={styles.docsList}>
          <li><strong>Tab Not Visible:</strong> Check your license status and user role. Some tabs require Pro license or specific permissions. Refresh the page to ensure permissions are current.</li>
          <li><strong>Changes Not Showing:</strong> Click the refresh button on the relevant tab. If still missing, clear your browser cache and do a hard refresh (Ctrl+Shift+R or Cmd+Shift+R).</li>
          <li><strong>Data Appears Stale:</strong> Admin changes may be cached. Refresh using tab controls or check the main shortcode page to see live data.</li>
          <li><strong>Form Won't Save:</strong> Ensure all required fields are filled. Check browser console (F12) for error messages. Verify your WordPress user has admin capabilities.</li>
          <li><strong>Missing Officers in Dropdowns:</strong> Add officers first in the Officers tab before assigning them to patrols or other entities.</li>
        </ul>
      </div>

      <div className={styles.card}>
        <h4>Best Practices</h4>
        <ul className={styles.docsList}>
          <li>Always configure General Settings and Appearance before officers access the portal.</li>
          <li>Test patrol types and structure with a few officers before scaling to full department.</li>
          <li>Regularly review Logs (Free) or Logs + API Views (Pro) to monitor system activity.</li>
          <li>Use descriptive names for patrol types and clear warning messages for critical information.</li>
          <li>Back up your WordPress database regularly to protect officer data and configuration.</li>
          <li>Keep the plugin and all dependencies updated for security and best performance.</li>
        </ul>
      </div>

      <div className={styles.card}>
        <h4>Support & Resources</h4>
        <p className={styles.helpText}>For additional help:</p>
        <ul className={styles.docsList}>
          <li>Check the WordPress admin pages under Law Enforcement Portal for detailed setup documentation.</li>
          <li>Review the plugin README file for installation, activation, and upgrade instructions.</li>
          <li>Contact your system administrator if you have questions about your specific licensing or permissions.</li>
        </ul>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralTab();
      case 'officers':
        return renderOfficersTab();
      case 'patrols-admin':
        return renderPatrolsTab();
      case 'change-requests':
        return renderRequestsTab();
      case 'view-stats':
        return renderViewStatsTab();
      case 'appearance':
        return renderAppearanceTab();
      case 'logs':
        return renderLogsTab();
      case 'api-views':
        return renderApiTab();
      case 'docs':
        return renderDocsTab();
      default:
        return renderGeneralTab();
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Admin Dashboard</h2>
      <div className={styles.content}>
        <div className={styles.visuallyHidden} aria-live="polite">{dragAnnouncement}</div>
        {!!message && <div className={styles.success} role="status" aria-live="polite">{message}</div>}
        {!!error && <div className={styles.error} role="alert">{error}</div>}
        {renderTabContent()}
      </div>
    </div>
  );
}

export default AdminDashboard;
